import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC } from "@/constants/testIds";

const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

/** Card de Ajustes: horario institucional de la clínica (distinto del texto
 * libre de la landing). Se usa para calcular disponibilidad real de turnos
 * y se muestra en landing/reserva pública/pie de página. */
export default function ClinicBusinessHoursCard({ clinic, onUpdated }) {
  const initial = (dayIndex) =>
    (clinic.business_hours || []).find((d) => d.day_of_week === dayIndex) || { day_of_week: dayIndex, open: "09:00", close: "18:00", is_active: false };
  const [hours, setHours] = useState(() => Array.from({ length: 7 }, (_, i) => initial(i)));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const updateDay = (dayIndex, patch) => {
    setHours((h) => h.map((d) => (d.day_of_week === dayIndex ? { ...d, ...patch } : d)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/clinic", { business_hours: hours });
      onUpdated(data);
      toast({ title: "Horarios de atención actualizados" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={CLINIC.businessHoursCard}>
      <div>
        <h2 className="font-serif text-xl text-foreground">Horarios de atención</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Se muestran en la landing, la página de reservas y el pie de página. El último turno del día puede
          empezar justo a la hora de cierre (ej: si cierras a las 16:30, todavía se puede reservar un turno a esa hora).
        </p>
      </div>
      <div className="border border-border rounded-lg divide-y divide-border">
        {DAY_NAMES.map((name, idx) => {
          const day = hours.find((d) => d.day_of_week === idx);
          return (
            <div key={idx} className="flex items-center gap-3 px-3 py-2.5 flex-wrap" data-testid={`${CLINIC.businessHoursDayPrefix}${idx}`}>
              <Switch checked={day.is_active} onCheckedChange={(v) => updateDay(idx, { is_active: v })} />
              <span className="text-sm w-24 shrink-0 text-foreground">{name}</span>
              <Input type="time" value={day.open} onChange={(e) => updateDay(idx, { open: e.target.value })} disabled={!day.is_active} className="w-28" />
              <span className="text-muted-foreground text-sm">a</span>
              <Input type="time" value={day.close} onChange={(e) => updateDay(idx, { close: e.target.value })} disabled={!day.is_active} className="w-28" />
            </div>
          );
        })}
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CLINIC.businessHoursSaveButton}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
    </div>
  );
}
